import type {
  MarketLadder,
  MarketLiquidityHeatmapSnapshot,
  MarketTrade,
} from '@hypersense/contracts/market';

import { marketFeedClient } from '@web/api/marketFeedClient';
import { ApiClientError, unwrapApiResult } from '@web/api/unwrapApiResult';

import type {
  MarketDefinitionT,
  MarketSummaryT,
  TradeWorkbenchIntervalT,
} from '../types';
import { MARKET_DEFAULT_ORDER_BOOK_LEVEL_COUNT } from './marketFeedDefaults';

export async function readMarketChartBars(input: {
  asset: string;
  chartInterval: TradeWorkbenchIntervalT;
  signal: AbortSignal;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    .chart.get({
      fetch: {
        signal: input.signal,
      },
      query: {
        interval: input.chartInterval,
      },
    });
  const response = unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} chart.`,
    result,
  });

  return response.bars;
}

export async function readMarkets(input: { signal: AbortSignal }) {
  const result = await marketFeedClient.markets.get({
    fetch: {
      signal: input.signal,
    },
  });
  const response = unwrapApiResult({
    fallbackMessage: 'Failed to load markets.',
    result,
  });

  return response.markets satisfies MarketDefinitionT[];
}

export async function readMarketDefinition(input: {
  asset: string;
  signal: AbortSignal;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    .get({
      fetch: {
        signal: input.signal,
      },
    });
  const response = unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} market definition.`,
    result,
  });

  return response satisfies MarketDefinitionT;
}

export function isAssetNotFoundError(error: unknown) {
  return error instanceof ApiClientError && error.status === 404;
}

export async function readMarketSummaries(input: { signal: AbortSignal }) {
  const result = await marketFeedClient.markets.summaries.get({
    fetch: {
      signal: input.signal,
    },
  });
  const response = unwrapApiResult({
    fallbackMessage: 'Failed to load market summaries.',
    result,
  });

  return response.summaries;
}

export async function readMarketSummary(input: {
  asset: string;
  signal: AbortSignal;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    .summary.get({
      fetch: {
        signal: input.signal,
      },
    });

  return unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} summary.`,
    result,
  }) satisfies MarketSummaryT;
}

export async function readMarketTrades(input: {
  asset: string;
  signal: AbortSignal;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    .trades.get({
      fetch: {
        signal: input.signal,
      },
    });
  const response = unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} trades.`,
    result,
  });

  return response.trades.map(normalizeHttpTrade);
}

export async function readMarketOrderBook(input: {
  asset: string;
  grouping: number;
  signal: AbortSignal;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    ['order-book'].get({
      fetch: {
        signal: input.signal,
      },
      query: {
        grouping: input.grouping,
      },
    });
  const response = unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} order book.`,
    result,
  });

  return normalizeHttpOrderBook(response);
}

export async function readMarketLiquidityHeatmap(input: {
  asset: string;
  signal: AbortSignal;
  windowMinutes: number;
}) {
  const result = await marketFeedClient
    .markets({
      asset: input.asset,
    })
    ['liquidity-heatmap'].get({
      fetch: {
        signal: input.signal,
      },
      query: {
        windowMinutes: input.windowMinutes,
      },
    });
  const response = unwrapApiResult({
    fallbackMessage: `Failed to load ${input.asset} liquidity heatmap.`,
    result,
  });

  return response satisfies MarketLiquidityHeatmapSnapshot;
}

function normalizeHttpTrade(input: {
  asset: string;
  notionalUsd: number;
  price: number;
  side: 'buy' | 'sell';
  size: number;
  timestampMs: number;
  tradeId: string;
  txHash: string;
}) {
  return {
    asset: input.asset,
    notional: input.notionalUsd,
    price: input.price,
    side: input.side,
    size: input.size,
    timestampMs: input.timestampMs,
    tradeId: input.tradeId,
    txHash: input.txHash,
  } satisfies MarketTrade;
}

function normalizeHttpOrderBook(input: {
  asks: {
    cumulativeSize: number;
    price: number;
    size: number;
  }[];
  asset: string;
  bestAskPrice: number;
  bestBidPrice: number;
  bids: {
    cumulativeSize: number;
    price: number;
    size: number;
  }[];
  grouping: number;
  midPrice: number;
  snapshotTimeMs: number;
  spreadBps: number;
}) {
  return {
    asks: input.asks.map((level) => {
      return {
        cumulative: level.cumulativeSize,
        price: level.price,
        size: level.size,
      };
    }),
    asset: input.asset,
    bestAskPrice: input.bestAskPrice,
    bestBidPrice: input.bestBidPrice,
    bids: input.bids.map((level) => {
      return {
        cumulative: level.cumulativeSize,
        price: level.price,
        size: level.size,
      };
    }),
    grouping: input.grouping,
    levels: MARKET_DEFAULT_ORDER_BOOK_LEVEL_COUNT,
    midPrice: input.midPrice,
    sequence: 0,
    spreadBps: input.spreadBps,
    timestampMs: input.snapshotTimeMs,
  } satisfies MarketLadder;
}
